import React, { useEffect, useRef, useContext } from 'react'
import { useRouter } from 'next/router'
import { useAnalytics } from '@pff-consumer/analytics'
import { useGetTeamsQuery, connectHooks, useGetCurrentEventWeekQuery } from '@pff-consumer/data-provider'
import { NFLWeekAbbreviation, ConnectScreens, CONNECT_ANALYTICS_EVENTS } from '@pff-consumer/schema'
import { ConnectAppContext } from '@pff-consumer/utils/lib/connect/connect-app-context'

import { Loading } from '../../common/loading/loading'
import { useStyles } from './layout.style'
import { getScreenNameFromPath } from '../../../utils/getScreenNameFromPath'

const { useConnectActions, useGetSelectedPlayerPosition, useGetSelectedPlayer } = connectHooks

export const Layout = ({ children }: { children: any }) => {
  const router = useRouter()
  const styles = useStyles()
  const { sendAnalyticsEvent } = useAnalytics()
  const { setSelectedWeek, setSelectedSeason } = useConnectActions()
  const selectedPlayerPosition = useGetSelectedPlayerPosition()
  const selectedPlayer = useGetSelectedPlayer()
  const mainLayoutRef = useRef<HTMLDivElement>(null)
  const { isPremiumSubscriber } = useContext(ConnectAppContext)

  useGetTeamsQuery()

  const { data, isLoading } = useGetCurrentEventWeekQuery()

  useEffect(() => {
    if (!mainLayoutRef.current) {
      return () => {}
    }

    // This app is embedded as an iframe so this ResizeObserver is used
    // to adjust the height of the iframe depending on the content's height
    // so that there are no scrollbars in the iframe
    const resizeObserver = new ResizeObserver(() => {
      const height = mainLayoutRef.current?.clientHeight
      window.parent.postMessage({ event: 'KEY_INSIGHTS_SET_IFRAME_HEIGHT', height }, '*')
    })

    resizeObserver.observe(mainLayoutRef.current)

    return () => {
      if (mainLayoutRef.current) {
        resizeObserver.unobserve(mainLayoutRef.current)
      }
    }
  }, [mainLayoutRef.current])

  const sendScreenLoadEvent = async (week: NFLWeekAbbreviation) => {
    const screenName: string = getScreenNameFromPath(router.pathname)
    sendAnalyticsEvent(CONNECT_ANALYTICS_EVENTS.VIEW, {
      experience: ConnectScreens.KEY_INSIGHTS,
      key_insight_tab: screenName,
      week,
      premium: isPremiumSubscriber,
    })
  }

  useEffect(() => {
    if (data?.abbreviation) {
      setSelectedWeek(data.abbreviation as NFLWeekAbbreviation)
      setSelectedSeason(data.season)
      sendScreenLoadEvent(data.abbreviation)
    }
  }, [data])

  useEffect(() => {
    mainLayoutRef.current?.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
  }, [selectedPlayerPosition, selectedPlayer])

  return (
    <div className={styles.layout}>
      <main
        ref={mainLayoutRef}
        className={`${styles.mainLayout} ${!isPremiumSubscriber ? styles.layoutPaywall : ''}`}
      >
        {isLoading ? <Loading /> : children}
      </main>
    </div>
  )
}
